import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, X } from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { removeDescription, removeSKU } from "../../Utils/skuDescriptionConverter";
import { zeroIfNegative } from "../../Utils/helper";
import SkuGraphPanel from "./treecombination/SkuGraphPanel";
import ReportPagination from "./ReportPagination";

export default function Report1() {
  const Product = useAppSelector((state) => state.workflow.selectedProduct);
  const Location = useAppSelector((state) => state.workflow.selectedLocation);

  const selectedModel = useAppSelector((state) => state.workflow.selectedModel);
  const selectedSkuIds = useAppSelector((state) => state.workflow.selectedSkuIds);
  const combinations = useAppSelector((state) => state.workflow.combinations);

  const bestActPredData = useAppSelector(
    (state) => state.workflow.bestActPredData
  );
  console.log("bestActPredDataaaaa", bestActPredData);
  const multipleActPredData = useAppSelector(
    (state) => state.workflow.multipleActPredData
  );
  console.log("multipleActPredDataaaaaaaaaa", multipleActPredData);

  const isBestModelMode = selectedModel === "bestModel";

  const dataSource = isBestModelMode
    ? bestActPredData
    : multipleActPredData;

  const skus = useMemo(() => {
    if (!combinations || !selectedSkuIds) return [];
    return combinations.filter((sku) =>
      selectedSkuIds.includes(sku.sku_id)
    );
  }, [combinations, selectedSkuIds]);

  const skuMap = useMemo(() => {
    return new Map((combinations || []).map((sku) => [sku.sku_name, sku]));
  }, [combinations]);

  const levelName = useAppSelector(
    (state) => state.workflow.selectedNode?.name || "Selected Level"
  );

  const [skuSummaryData, setSkuSummaryData] = useState([]);
  const [skuTablePeriods, setSkuTablePeriods] = useState([]);
  const [showChart, setShowChart] = useState(false);
  const [chartModalSku, setChartModalSku] = useState(null);

  // 🔥 PAGINATION STATES
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // Show 20 SKUs per page

  const selectedModelName = selectedModel;

  // 🔥 FIXED: Correctly extract city data from new structure
  // 🔥 SUPER FIXED: Handles BOTH data structures dynamically
  const cityData = useMemo(() => {
    console.log("🔍 dataSource full structure:", dataSource);

    if (!dataSource || dataSource?.status !== "success") {
      console.log("❌ Invalid dataSource");
      return null;
    }

    let cities = [];

    // 🔥 Try best model structure first: data.data.cities
    if (dataSource.data?.data?.cities) {
      console.log("✅ Using best model structure: data.data.cities");
      cities = dataSource.data.data.cities;
    }
    // 🔥 Try multiple model structure: data.cities  
    else if (dataSource.data?.cities) {
      console.log("✅ Using multiple model structure: data.cities");
      cities = dataSource.data.cities;
    } else {
      console.log("❌ No cities found in either structure", dataSource.data);
      return null;
    }

    console.log("🔍 Cities found:", cities);

    if (!cities.length) {
      console.log("❌ No cities");
      return null;
    }

    const targetCity = Location || cities[0]?.city;
    const foundCity = cities.find((c) => c.city === targetCity);

    console.log("✅ City Found:", foundCity);
    console.log("✅ City data length:", foundCity?.data?.length);

    return foundCity || null;
  }, [dataSource, Location]);

  console.log("SKU from UI:", skus && skus.map(s => s.sku_name));
  console.log("SKU from API:", cityData && cityData?.data?.map(d => d.sku));

  // 🔥 FIXED: Correctly process new nested data structure
  useEffect(() => {
    if (!cityData?.data?.length || !skus.length) {
      console.log("❌ Missing data:", { cityData: !!cityData, skusLength: skus?.length });
      setShowChart(false);
      setSkuSummaryData([]);
      setSkuTablePeriods([]);
      setCurrentPage(1); // Reset pagination
      return;
    }

    try {
      const citySkuList = cityData?.data || [];

      const matchedSkuData = skus
        .map((skuNode) => {
          const skuName = (skuNode?.sku_name || "")
            .toLowerCase()
            .trim();

          return citySkuList.find((item) => {
            const apiSku = (item?.sku || "")
              .toLowerCase()
              .trim();

            return apiSku === skuName;
          });
        })
        .filter(Boolean);

      if (!matchedSkuData.length) {
        setShowChart(false);
        setSkuSummaryData([]);
        setSkuTablePeriods([]);
        setCurrentPage(1); // Reset pagination
        return;
      }

      console.log("Selected Model:", selectedModel);

      const periodSet = new Set();

      // 🔥 FIXED: Handle both data structures correctly
      const skuSummaryRows = matchedSkuData.map((skuItem) => {
        let rows = [];

        if (isBestModelMode) {
          rows = skuItem?.data || [];
        } else {
          const modelBlock = skuItem?.multiple_model_data?.find(
            (model) => model.model_name === selectedModel
          );
          rows = modelBlock?.data || [];
        }

        rows.forEach((row) => periodSet.add(row.frequency_year));

        return {
          sku: skuItem.sku,
          top_model_name: skuItem?.top_model_name || "",
          rows,
        };
      });

      setSkuSummaryData(skuSummaryRows);
      // 🔥 Sort latest month first (descending chronological order)
      const sortedPeriods = Array.from(periodSet).sort((a, b) => {
        const [aMonth, aYear] = a.split("-").map(Number);
        const [bMonth, bYear] = b.split("-").map(Number);
        return new Date(aYear, aMonth - 1) - new Date(bYear, bMonth - 1);
      }).reverse(); // Reverse for latest first

      setSkuTablePeriods(sortedPeriods);

      // 🔥 FIXED: Aggregation works with new structure
      const periodMap = new Map();

      matchedSkuData.forEach((skuItem) => {
        let modelRows = [];

        if (isBestModelMode) {
          // ✅ BEST MODEL → direct rows
          modelRows = skuItem?.data || [];
        } else {
          // ✅ MULTIPLE MODEL → selected model's data array
          const modelBlock = skuItem?.multiple_model_data?.find(
            (model) => model.model_name === selectedModel
          );
          modelRows = modelBlock?.data || [];
        }

        modelRows.forEach((row) => {
          const period = row.frequency_year;

          if (!periodMap.has(period)) {
            periodMap.set(period, {
              period,
              actual: 0,
              predicted: 0,
              skuCount: 0,
            });
          }

          const existing = periodMap.get(period);
          existing.actual += Number(row.actual || 0);
          existing.predicted += Number(row.predicted || 0);
          existing.skuCount += 1;
        });
      });

      const aggregated = Array.from(periodMap.values());

      if (!aggregated.length) {
        setShowChart(false);
        return;
      }

      setShowChart(true);
      setCurrentPage(1); // Reset to first page when data changes
    } catch (error) {
      console.error("Error in Report1 aggregation:", error);
      setShowChart(false);
      setSkuSummaryData([]);
      setSkuTablePeriods([]);
      setCurrentPage(1); // Reset pagination
    }
  }, [cityData, skus, selectedModel, isBestModelMode]);

  // 🔥 PAGINATION LOGIC
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSkus = skuSummaryData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(skuSummaryData.length / itemsPerPage));

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (!showChart) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: "#6a7d8c",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <p style={{ fontSize: 14, fontWeight: 600, color: "#0c3c54", margin: 0 }}>
          No Actual vs Predicted Data
        </p>
        <p style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
          Data not found for {levelName || Product} in {Location}
        </p>
      </div>
    );
  }

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return Number(value).toLocaleString();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        flex: 1,
        paddingBottom: 8,
      }}
    >
      {/* Table card scrolls independently; pagination stays outside below.
          minHeight is sized for ~10 SKUs (4 stacked rows each: Actual,
          Predicted, Forecasted label, Forecasted value) so a normal page
          shows 10 before the user has to scroll, instead of shrinking down
          to whatever flex space happens to be left over. */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e6eef4",
          borderRadius: 10,
          overflowX: "auto",
          overflowY: "auto",
          minHeight: 1200,
          marginTop: 16,
          position: "relative",
          flex: "1 1 auto",
        }}
      >
        <table
          style={{
            width: "100%",
            minWidth: 1400,
            borderCollapse: "collapse",
            fontFamily: "Poppins, sans-serif",
            background: "#fff",
          }}
        >
          <thead>
            <tr
              style={{
                background: "#032B4E",
                borderBottom: "1px solid #e6eef4",
              }}
            >
              <th style={headerCellLeft}>SKU</th>
              <th style={headerCellLeft}>Description</th>
              <th style={headerCellLeft}>Model</th>
              <th style={headerCellLeft}>Type</th>
              {skuTablePeriods
                .filter((p) => {
                  const sampleRow = skuSummaryData?.[0]?.rows?.find((r) => r.frequency_year === p);
                  return sampleRow?.ftype !== "forecast_forecast";
                })
                .map((period, idx) => (  // 🔥 Use idx instead of period for key
                  <th key={`header-${idx}`} style={headerCellCenter}>
                    {period}
                  </th>
                ))}
            </tr>
          </thead>

          <tbody>
            {currentSkus.map((item, skuIndex) => {  // 🔥 Only render current page SKUs
              const normalRows = item.rows.filter((r) => r.ftype !== "forecast_forecast");
              const forecastRows = item.rows.filter((r) => r.ftype === "forecast_forecast");

              const skuInfo = skuMap.get(item.sku);
              const skuCode = skuInfo?.sku_code || skuInfo?.sku_name || skuInfo?.name || item.sku;
              const modelLabel = selectedModel === "bestModel"
                ? item.top_model_name || "Best Model"
                : selectedModel;

              return (
                <React.Fragment key={item.sku}>
                  {/* Actual row */}
                  <tr style={{ borderBottom: "1px solid #e6eef4" }}>
                    <td rowSpan={4} style={skuCellStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{removeDescription(skuCode)}</span>
                        <button
                          onClick={() => setChartModalSku(skuInfo || item)}
                          title="View Actual / Predicted / Forecasted chart"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 4,
                            borderRadius: 10,
                            border: "1px solid #f0d2bd",
                            background: "#fff6ee",
                            color: "#E06639",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        >
                          <BarChart3 size={14} />
                        </button>
                      </div>
                    </td>
                    <td rowSpan={4} style={descCellStyle}>
                      {removeSKU(skuCode)}
                    </td>
                    <td rowSpan={4} style={descCellStyle}>
                      {modelLabel}
                    </td>
                    <td style={typeCellStyle}>
                      <span style={{ color: "#2f67d8", fontWeight: 600 }}>Actual</span>
                    </td>

                    {/* Period columns */}
                    {skuTablePeriods
                      .filter((p) => {
                        const row = normalRows.find((r) => r.frequency_year === p);
                        return row && row.ftype !== "forecast_forecast";
                      })
                      .map((period, i) => {
                        const row = normalRows.find((r) => r.frequency_year === period);
                        return (
                          <td key={`actual-${period}`} style={valueCellStyle}>
                            {formatNumber(row?.actual || 0)}
                          </td>
                        );
                      })}
                  </tr>

                  {/* Predicted row */}
                  <tr style={{ borderBottom: "1px solid #e6eef4" }}>
                    <td style={typeCellStyle}>
                      <span style={{ color: "#2ca24f", fontWeight: 600 }}>Predicted</span>
                    </td>

                    {skuTablePeriods
                      .filter((p) => {
                        const row = normalRows.find((r) => r.frequency_year === p);
                        return row && row.ftype !== "forecast_forecast";
                      })
                      .map((period, i) => {
                        const row = normalRows.find((r) => r.frequency_year === period);
                        return (
                          <td key={`predicted-${period}`} style={valueCellStyle}>
                            {formatNumber(zeroIfNegative(row?.predicted || 0))}
                          </td>
                        );
                      })}
                  </tr>

                  {/* Rest of your forecast rows remain the same */}
                  <tr style={{ borderBottom: "1px solid #f0e1d8" }}>
                    <td
                      style={{
                        ...typeCellStyle,
                        background: "#fdf1ec",
                      }}
                      rowSpan={2}
                    >
                      <span style={{ color: "#7a35c8", fontWeight: 600 }}>Forecasted</span>
                    </td>
                    {forecastRows.map((row, i) => (
                      <td
                        key={`fm-${i}`}
                        style={{
                          ...forecastMonthCellStyle,
                          background: "#fdf1ec",
                        }}
                      >
                        {row.frequency_year}
                      </td>
                    ))}
                  </tr>

                  <tr
                    style={{
                      borderBottom:
                        skuIndex === currentSkus.length - 1 ? "none" : "1px solid #d9d9d9",
                    }}
                  >
                    {forecastRows.map((row, i) => (
                      <td
                        key={`fv-${i}`}
                        style={{
                          ...forecastValueCellStyle,
                          background: "#fffaf7",
                        }}
                      >
                        {formatNumber(zeroIfNegative(row.predicted))}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <ReportPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        infoText={`Page ${currentPage} of ${totalPages} (${skuSummaryData.length} SKUs)`}
      />

      {/* Chart modal */}
      {chartModalSku && (
        <div
          onClick={() => setChartModalSku(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#f8fafc",
              borderRadius: 16,
              width: "min(900px, 95vw)",
              maxHeight: "90vh",
              overflowY: "auto",
              position: "relative",
              padding: "20px 8px 8px",
              boxSizing: "border-box",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <button
              onClick={() => setChartModalSku(null)}
              title="Close"
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 1,
                padding: 6,
                borderRadius: 10,
                border: "none",
                background: "#fff",
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                cursor: "pointer",
                color: "#032B4E",
              }}
            >
              <X size={18} />
            </button>
            <SkuGraphPanel
              skuItem={chartModalSku}
              onClose={() => setChartModalSku(null)}
              Product={Product}
              Location={Location}
              bestActPredData={bestActPredData}
              multipleActPredData={multipleActPredData}
              selectedModel={selectedModel}
              compact
            />
          </div>
        </div>
      )}
    </div>
  );
}

// 🔥 STYLES (unchanged)
const headerCellLeft = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  background: "#032B4E",
  color: "#fff",
  textAlign: "left",
  padding: "8px 10px",
  whiteSpace: "nowrap",
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  borderBottom: "1px solid #e6eef4",
};

const headerCellCenter = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  background: "#032B4E",
  color: "#fff",
  textAlign: "center",
  padding: "8px 10px",
  whiteSpace: "nowrap",
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  borderBottom: "1px solid #e6eef4",
};

const skuCellStyle = {
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  color: "#0c3c54",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  borderRight: "1px solid #e6eef4",
  fontFamily: "Poppins, sans-serif",
};

const descCellStyle = {
  padding: "8px 10px",
  fontSize: 12,
  lineHeight: 1.35,
  color: "#6a7d8c",
  verticalAlign: "middle",
  minWidth: 110,
  maxWidth: 160,
  borderRight: "1px solid #e6eef4",
  fontFamily: "Poppins, sans-serif",
};

const typeCellStyle = {
  padding: "6px 10px",
  fontSize: 12,
  textAlign: "left",
  whiteSpace: "nowrap",
  minWidth: 90,
  borderRight: "1px solid #e6eef4",
  fontFamily: "Poppins, sans-serif",
};

const valueCellStyle = {
  padding: "6px 10px",
  fontSize: 12,
  color: "#0c3c54",
  textAlign: "center",
  whiteSpace: "nowrap",
  borderRight: "1px solid #eef2f6",
  fontFamily: "Poppins, sans-serif",
};

const forecastMonthCellStyle = {
  padding: "6px 10px",
  fontSize: 12,
  color: "#0c3c54",
  textAlign: "center",
  whiteSpace: "nowrap",
  fontWeight: 600,
  borderRight: "1px solid #f3e8e0",
  fontFamily: "Poppins, sans-serif",
};

const forecastValueCellStyle = {
  padding: "6px 10px",
  fontSize: 12,
  color: "#0c3c54",
  textAlign: "center",
  whiteSpace: "nowrap",
  fontWeight: 700,
  borderRight: "1px solid #f3e8e0",
  fontFamily: "Poppins, sans-serif",
};